import { useSelector } from "react-redux";
import { useUserData } from "./useUserData";
import { useUserFiltering } from "./useUserFiltering";
import { useUserEditing } from "./useUserEditing";
import { useUserActions } from "./useUserActions";
import { useUserSelection } from "./useUserSelection";
import { useWorkspaceMarkets } from "./useWorkspaceMarkets";
import { MODULE_SCOPES } from "../../constants/moduleScopes";

export const useUserManagement = () => {
  const currentUserEmail = useSelector((state) => state.auth?.user?.email);
  // Initialize workspace markets hook for dynamic region/market data
  const {
    currentClientRegionMarketData, // Filtered by current user's id_client
    hierarchicalWorkspaces,
    workspaceOptions,
    getRegionMarketDataForWorkspace,
    getMarketsForWorkspace,
    generateTreeDataForWorkspace,
    fetchingWorkspaces,
  } = useWorkspaceMarkets();

  // Initialize user data hook
  const {
    users,
    setUsers,
    activeUsers,
    deactivatedUsers,
    activeCount,
    deactivatedCount,
    loading,
    hasFetchedData,
    assignableRoles,
    statusFilter,
    setStatusFilter,
    contextHolder: dataContextHolder,
    messageApi: dataMessageApi,
    regionMarketData: staticRegionMarketData,
    getMarketScope,
    getBaseUserKey,
    // getUserRowsByBaseKey,
    // flattenMarketSelections,
    // processMarketSelections,
    // createUserRowsForMarkets,
    // initializeMarketSelections,
    // loadUsers,
    // New functions for hierarchical data
    calculateRowSpans,
    reloadUsers,
  } = useUserData();

  // Use dynamic region-market data filtered by current user's id_client
  // Falls back to static data if no dynamic data available
  const regionMarketData =
    currentClientRegionMarketData &&
    Object.keys(currentClientRegionMarketData).length > 0
      ? currentClientRegionMarketData
      : staticRegionMarketData;

  // Initialize filtering hook
  const {
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    filteredUsers,
    paginatedUsers,
    uniqueUserCount,
    deactivatedMatchCount,
    handlePaginationChange,
    handlePageSizeChange,
  } = useUserFiltering(users, deactivatedUsers);

  // Initialize selection hook
  const { selectedRows, setSelectedRows, actualSelectedCount, rowSelection } =
    useUserSelection(filteredUsers);

  // Augment rowSelection with renderCell so multi-row users share one checkbox
  // and the current user's row has no checkbox.
  const rowSelectionWithSpan = {
    ...rowSelection,
    renderCell: (checked, record, _index, originNode) => {
      const userRows = paginatedUsers.filter(
        (u) => u.userId === record.userId
      );
      const isFirstRow = userRows[0]?.key === record.key;

      // Current user: render one empty cell spanning all their rows (no checkbox),
      // so columns stay aligned.
      if (record.email === currentUserEmail) {
        return {
          props: { rowSpan: isFirstRow ? userRows.length : 0 },
          children: null,
        };
      }

      return {
        props: { rowSpan: isFirstRow ? userRows.length : 0 },
        children: isFirstRow ? originNode : null,
      };
    },
  };

  // Initialize editing hook with dynamic region-market data
  const {
    editingRows,
    setEditingRows,
    openDropdowns,
    setOpenDropdowns,
    selectedMarkets,
    setSelectedMarkets,
    expandedRegions,
    setExpandedRegions,
    stagingData,
    setStagingData,
    startEditUser,
    stopEditUser,
    handleEditUser,
    handleSaveUser,
    handleStageChange,
    handleMarketDropdownOpenChange,
    handleRoleDropdownOpenChange,
    handleProductDropdownOpenChange,
    handleMarketSelection,
    handleRegionExpansion,
    handleRegionSelection,
    getDisplayValue,
    isRegionFullySelected,
    isMarketSelected,
    getRegionMarketData,
    isSaving,
  } = useUserEditing(users, setUsers, getBaseUserKey, {
    // Pass dynamic region-market data from Redux
    regionMarketData,
    // Optional: per-user workspace data function
    // getRegionMarketDataForUser: (user) => getRegionMarketDataForWorkspace(user.workspaceId),
    // Callbacks for save operation
    onSaveSuccess: (message) => {
      dataMessageApi.success(message);
    },
    onSaveError: (message) => {
      dataMessageApi.error(message);
    },
  });

  // Add a second module row for a user that currently only has one
  const handleAddModule = (user) => {
    const userRows = users.filter((u) => u.userId === user.userId);
    const existingModules = userRows.map((r) => r.productName);
    const allModules = MODULE_SCOPES.map((m) => m.label);
    const missingModule = allModules.find((m) => !existingModules.includes(m));
    if (!missingModule) return;

    const tempKey = `temp-${user.userId}-${Date.now()}`;
    const tempRow = {
      ...user,
      key: tempKey,
      productName: missingModule,
      product: missingModule,
      isTemporary: true,
      productIndex: 1,
      totalProductsForRole: 2,
    };

    setUsers((prev) => {
      const lastIdx = prev.reduce(
        (acc, u, i) => (u.userId === user.userId ? i : acc),
        -1
      );
      const next = [...prev];
      next.splice(lastIdx + 1, 0, tempRow);
      return next;
    });

    startEditUser(tempRow);
  };

  // Initialize actions hook
  const {
    // Invite Modal
    isInviteModalVisible,
    handleInviteUser,
    handleInviteModalOk,
    handleInviteModalCancel,
    form,

    // Delete Modal
    deleteModalVisible,
    userToDelete,
    deleteType,
    isDeleting,
    handleDeleteUser,
    confirmDeleteUser,
    cancelDeleteUser,

    // Deactivated-view actions
    handleReactivateUser,
    handlePermanentDelete,
    isReactivating,

    // Bulk Delete Modal
    bulkDeleteModalVisible,
    handleBulkDelete,
    confirmBulkDelete,
    cancelBulkDelete,

    // Success Modal
    successModalVisible,
    deletedUser,
    bulkDeletedCount,
    bulkDeletedMarkets,
    deletedUsers,
    isRestoringUsers,
    handleRestoreUsers,
    handleSuccessModalClose,

    // Export
    handleExport,

    // Message API
    messageApi: actionsMessageApi,
    contextHolder: actionsContextHolder,
  } = useUserActions(users, setUsers, selectedRows, setSelectedRows, reloadUsers);

  // Combine context holders and message APIs
  const contextHolder = (
    <>
      {dataContextHolder}
      {actionsContextHolder}
    </>
  );

  const messageApi = {
    ...dataMessageApi,
    ...actionsMessageApi,
  };

  return {
    // Data
    users,
    setUsers,
    activeUsers,
    loading,
    hasFetchedData,
    assignableRoles,
    statusFilter,
    setStatusFilter,
    regionMarketData,
    getMarketScope,

    // Workspace data (from Redux)
    hierarchicalWorkspaces,
    workspaceOptions,
    getRegionMarketDataForWorkspace,
    getMarketsForWorkspace,
    generateTreeDataForWorkspace,
    fetchingWorkspaces,

    // Filtering & Pagination
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    filteredUsers,
    paginatedUsers,
    uniqueUserCount,
    // Per-tab counts + the deactivated-search-match count (empty-search hint)
    activeCount,
    deactivatedCount,
    deactivatedMatchCount,
    handlePaginationChange,
    handlePageSizeChange,

    // Selection
    selectedRows,
    setSelectedRows,
    actualSelectedCount,
    rowSelection: rowSelectionWithSpan,

    // Editing
    editingRows,
    setEditingRows,
    openDropdowns,
    setOpenDropdowns,
    selectedMarkets,
    setSelectedMarkets,
    expandedRegions,
    setExpandedRegions,
    stagingData,
    setStagingData,
    startEditUser,
    stopEditUser,
    handleAddModule,
    handleEditUser,
    handleSaveUser,
    handleStageChange,
    handleMarketDropdownOpenChange,
    handleRoleDropdownOpenChange,
    handleProductDropdownOpenChange,
    handleMarketSelection,
    handleRegionExpansion,
    handleRegionSelection,
    getDisplayValue,
    isRegionFullySelected,
    isMarketSelected,
    getRegionMarketData,
    isSaving,

    // Actions & Modals
    isInviteModalVisible,
    handleInviteUser,
    handleInviteModalOk,
    handleInviteModalCancel,
    form,
    deleteModalVisible,
    userToDelete,
    deleteType,
    isDeleting,
    handleDeleteUser,
    confirmDeleteUser,
    cancelDeleteUser,
    handleReactivateUser,
    handlePermanentDelete,
    isReactivating,
    bulkDeleteModalVisible,
    handleBulkDelete,
    confirmBulkDelete,
    cancelBulkDelete,
    successModalVisible,
    deletedUser,
    bulkDeletedCount,
    bulkDeletedMarkets,
    deletedUsers,
    isRestoringUsers,
    handleRestoreUsers,
    handleSuccessModalClose,
    handleExport,

    // Message API & Context
    messageApi,
    contextHolder,

    // New functions for hierarchical data
    calculateRowSpans,
  };
};
